import { useState } from "react";
import { useDeal } from "./store";
import { parseApi, streamPost, streamPostForm } from "./api";
import { toast } from "sonner";

export const num = (x) => (x === null || x === undefined || x === "" || isNaN(Number(x)) ? undefined : Number(x));

export function mapFields(f) {
  const out = {};
  const set = (k, v) => { if (v !== undefined && v !== null && v !== "") out[k] = v; };
  set("dealName", f.dealName);
  set("companyName", f.companyName);
  set("industry", f.industry);
  set("address", f.address);
  set("askPrice", num(f.askingPrice));
  set("ttmRevenue", num(f.revenue));
  set("grossMarginPct", num(f.grossMarginPct));
  set("sde", num(f.adjustedSDE));
  set("officerSalaryAddback", num(f.officerSalaryAddback));
  set("employeeCount", num(f.employeeCount));
  set("yearEstablished", num(f.yearEstablished));
  set("reVal", num(f.realEstateValue));
  set("nwc", num(f.nwcInventory));
  set("ffe", num(f.ffAndE));
  set("goodwill", num(f.goodwill));
  set("reasonForSale", f.reasonForSale);
  set("brokerName", f.brokerName);
  set("brokerPhone", f.brokerPhone);
  if (Array.isArray(f.historicalYears) && f.historicalYears.length) {
    out.historicalYears = (f.historicalYears || []).map((h) => ({
      year: String(h.year ?? ""), revenue: num(h.revenue) || 0, gm: num(h.grossMarginPct) || 0,
      netIncome: num(h.netIncome) || 0, sde: num(h.sde) || 0,
    }));
  }
  return out;
}

export const parseLoose = (t) => {
  let s = (t || "").trim();
  if (s.startsWith("```")) s = s.replace(/^```(json)?/i, "").replace(/```\s*$/, "");
  const a = s.indexOf("{"), b = s.lastIndexOf("}");
  if (a !== -1 && b !== -1) s = s.slice(a, b + 1);
  return JSON.parse(s);
};

// Parses a CIM / P&L / financial statement and patches the live model. `only` restricts which fields land.
export function useFinancialParse() {
  const { patch, setDealName, requestSave } = useDeal();
  const [busy, setBusy] = useState(false);
  const parseFile = async (file, { only, renameDeal = true } = {}) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      let full = "", meta = { filename: file.name };
      if (file.name.toLowerCase().endsWith(".pdf")) {
        await streamPostForm("/parse/analyze-pdf", fd, (c, f) => { full = f; });
      } else {
        const ex = await parseApi.extractText(fd);
        meta = ex;
        await streamPost("/parse/analyze", { text: ex.text, kind: ex.kind }, (c, f) => { full = f; });
      }
      const parsed = parseLoose(full) || {};
      let mapped = mapFields(parsed);
      if (only) mapped = Object.fromEntries(Object.entries(mapped).filter(([k]) => only.includes(k)));
      patch(mapped);
      if (renameDeal && mapped.dealName) setDealName(mapped.dealName);
      setTimeout(requestSave, 50);
      return { raw: parsed, mapped, meta };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed — the file may be unreadable");
      throw e;
    } finally { setBusy(false); }
  };
  return { parseFile, busy };
}
